import React, { useEffect } from "react";
import { Link } from "react-router-dom";

const About: React.FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const features = [
    {
      title: "Subjects",
      copy: "Knowledge is organized into a tree of subjects. This hierarchical structure allows you to zoom in on specific topics or zoom out to see the broader context.",
    },
    {
      title: "Gists",
      copy: "A gist is the core unit of information. It consists of a title, a one-sentence summary, and a detailed body. It's designed to be richer than a comment but more focused than a blog post.",
    },
    {
      title: "Reader & Discussion Views",
      copy: "Switch between Reader View for a clean, linear reading experience and Discussion View to visualize the complex branching of replies and arguments.",
    },
    {
      title: "Anchored Comments",
      copy: "Comments can be anchored to specific parts of a gist's body, allowing for precise feedback and contextual discussions that stay relevant to the text.",
    },
  ];

  return (
    <main className="home-landing-page" aria-label="About Gistoscope">
      <section className="home-hero-section">
        <div className="home-hero-copy">
          <div className="home-kicker">About the platform</div>
          <h1 className="home-hero-title">Beyond the Feed</h1>
          <p className="home-hero-lede">
            Gistoscope is a specialized tool for structured knowledge navigation. 
            Unlike traditional forums or social feeds that prioritize the newest content, 
            Gistoscope prioritizes the <strong>structure</strong> of ideas and the 
            <strong>clarity</strong> of claims.
          </p>

          <div className="home-hero-actions" aria-label="About navigation">
            <Link to="/" className="home-action-primary">
              Explore Subject Tree
            </Link>
            <Link to="/home" className="home-action-secondary">
              Go to Home
            </Link>
          </div>
        </div>

        <aside className="home-hero-card" aria-label="Why Gistoscope is different">
          <div className="home-hero-card-label">Why it's different</div>
          <div className="home-gist-preview-card">
            <div className="home-gist-preview-title">Structure Over Chronology</div>
            <div className="home-gist-preview-summary">
              Ideas are durable blocks, not just transient posts in a stream.
            </div>
            <div className="home-gist-preview-body">
              By organizing knowledge into gists and subjects, we create a map that remains 
              useful long after a discussion is over.
            </div>
          </div>
          <p className="home-hero-card-note">
            Built for those who prefer deep navigation over endless scrolling.
          </p>
        </aside>
      </section>

      <section className="home-section">
        <div className="home-section-heading">
          <div className="home-kicker">How it works</div>
          <h2>A Map of Connected Thought</h2>
          <p>
            Gistoscope bridges the gap between static documentation and chaotic discussion. 
            It is designed for moving between overview and detail without losing your place.
          </p>
        </div>

        <div className="home-workflow-grid">
          {features.map((feature) => (
            <article key={feature.title} className="home-workflow-card">
              <div className="home-workflow-eyebrow">Feature</div>
              <h3>{feature.title}</h3>
              <p>{feature.copy}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="home-section home-split-section">
        <div className="home-section-heading home-section-heading-compact">
          <div className="home-kicker">The Vision</div>
          <h2>Better Discourse</h2>
          <p>
            Standard forums often suffer from "context collapse" where many people talk past 
            each other. Gistoscope uses subjects and anchored comments to keep everyone 
            on the same page—literally.
          </p>
        </div>

        <div className="home-mode-grid">
          <article className="home-mode-card">
            <div className="home-mode-label">Clarity</div>
            <h3>Summary-First Thinking</h3>
            <p>
              Every gist starts with a one-sentence summary. If you can't summarize your 
              claim, you might not be ready to post it.
            </p>
          </article>

          <article className="home-mode-card">
            <div className="home-mode-label">Depth</div>
            <h3>Connected Context</h3>
            <p>
              Replies aren't just a list; they are a tree. See how one argument leads 
              to another, and how evidence supports or refutes a claim.
            </p>
          </article>
        </div>
      </section>

      <section className="home-section home-closing-section">
        <div>
          <div className="home-kicker">Get started</div>
          <h2>Ready to explore?</h2>
          <p>
            Dive into the Subject Tree to see what the community is building, or check 
            out the Home page for more introductory material.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-4">
          <Link to="/" className="home-action-primary home-closing-action">
            Open Subject Tree
          </Link>
          <Link to="/home" className="home-action-secondary home-closing-action" style={{ background: 'white' }}>
            Visit Home
          </Link>
        </div>
      </section>
    </main>
  );
};

export default About;
